/**
 * Кореневий маршрут: оболонка і метадані. Після входу корінь чекає `/meta` платформи (ранній
 * запит у public/boot.js): з нього оболонка будує навігацію за правами одним рендером, без
 * пунктів, що з'являються пізніше. Без входу корінь нічого не чекає: «/» і картка ведуть на вхід —
 * окремий документ поза застосунком (login.html; routes/login.ts).
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, type ErrorComponentProps } from '@tanstack/react-router';
import { lazy, type ReactNode, Suspense } from 'react';
import { hasSessionHint, platformMetaQuery } from '../../platform/meta.ts';
import { AppShell } from '../../shell/AppShell.tsx';
import { isRequestFailure } from '../../support/classify.ts';
import { RootError } from '../../support/RootError.tsx';
import { RetryBoundary } from '../../ui/RetryBoundary.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

export { LOGIN_PATH } from '../next-path.ts';

const NotFoundView = lazy(() =>
  import('./route-errors.tsx').then((m) => ({ default: m.NotFoundView })),
);
const RouteErrorView = lazy(() =>
  import('./route-errors.tsx').then((m) => ({ default: m.RouteErrorView })),
);

/**
 * Лінивий стан помилки: чанк з текстами не доїхав (обрив, стара вкладка після релізу) — замість
 * нього запасний екран «Інтерфейс зупинився» з перезавантаженням (support/RootError.tsx, у головному
 * чанку). Поки чанк їде, порожньо: це кілька десятків мілісекунд після збою, не звичайний шлях.
 */
function LazyState({ children }: { children: ReactNode }) {
  return (
    <RetryBoundary fallback={() => <RootError error={null} embedded />}>
      <Suspense fallback={null}>{children}</Suspense>
    </RetryBoundary>
  );
}

/** Сторінка «не знайдено» в робочій області: оболонка лишається (UX-50: пошук і стартовий екран). */
export function NotFound() {
  return (
    <LazyState>
      <NotFoundView />
    </LazyState>
  );
}

/** Помилка маршруту (UX-01: збій однієї області не валить решту); вигляд — route-errors.tsx. */
export function RouteError(props: ErrorComponentProps) {
  return (
    <LazyState>
      <RouteErrorView {...props} />
    </LazyState>
  );
}

/**
 * Межа кореневого маршруту. Сюди роутер віддає й помилки завантаження даних екранів (збій запиту:
 * `RouteError` з видом за статусом), і винятки в самому коді оболонки (сторінка «Інтерфейс
 * зупинився через помилку» з перезавантаженням і зверненням, без роутера й запитів).
 */
function RootRouteError(props: ErrorComponentProps) {
  return isRequestFailure(props.error) ? (
    <RouteError {...props} />
  ) : (
    <RootError error={props.error} />
  );
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  loader: async ({ context }) => {
    if (!hasSessionHint()) return;
    // Збій тут не валить екран: оболонка тоді покаже навігацію без входу, а екран — свій стан.
    await context.queryClient.ensureQueryData(platformMetaQuery()).catch(() => undefined);
  },
  component: AppShell,
  notFoundComponent: NotFound,
  errorComponent: RootRouteError,
});
